import { type DraftTargetLink, type IdentifierRecord, type SectorId } from '@myplayer/schema';
import { useMemo, useState, type FormEvent } from 'react';
import { useAuth } from './auth-context.js';
import {
  applyLocalDraftRecordUpdate,
  usePendingPromotionTracker,
} from './product-review-changes.js';
import { useProductsCsvController } from './products-csv-panel.js';
import {
  clearProductRecallAction,
  computeDuplicateIdentifierConflicts,
  computeProductFacetCounts,
  confirmProductRecallAction,
  createProductDraftAction,
  filterWorkspaceIdentifierRecords,
  stageDraftLinksAction,
  type RecallActionOutcome,
} from './products-helpers.js';
import { computeProductQrPreview, downloadProductQrAsset } from './products-recall-helpers.js';
import {
  type GeneratedGs1QrPrintAssets,
  type Gs1SymbolSpecTableId,
  type QrExportFormat,
} from './qr-generator.js';
import { useProductEditState } from './use-product-edit-state.js';
import { useProductsRouterNavigation } from './use-products-router-sync.js';
import {
  resolveDefaultProductsViewMode,
  resolveEffectiveHost,
  useProductsFilterAndCreateState,
  useWorkspaceIdentifiers,
  useWorkspaceTargetMeta,
  type ProductsControllerParams,
} from './use-workspace-identifiers.js';

export { resolveDefaultProductsViewMode, type ProductsControllerParams };

export function useProductsController(params: ProductsControllerParams) {
  const effectiveResolverHost = resolveEffectiveHost(params.resolverHost);
  const { session, can, availableWorkspaces, workspaceScope, setWorkspaceScope } = useAuth();
  const canEdit = can('identifier:create_stage_update');
  const canRecall = can('recall:activate_deactivate');
  const canExport = can('analytics:export');

  const [workspaceFilter, setWorkspaceFilter] = useState<string>('ALL');
  const workspaceMeta = useWorkspaceTargetMeta(
    session,
    availableWorkspaces,
    workspaceScope,
    workspaceFilter,
  );
  const { targetWorkspaceIds, allOrgWorkspaceIds, workspaceNameMap } = workspaceMeta;

  const {
    records,
    allOrgRecords,
    setRecords,
    selectedKey,
    setSelectedKey,
    loading,
    listenerError,
  } = useWorkspaceIdentifiers(session, params.initialRecords, params.initialSelectedKey, {
    ...(targetWorkspaceIds.length > 0 ? { workspaceIds: targetWorkspaceIds } : {}),
    ...(allOrgWorkspaceIds.length > 0 ? { allWorkspaceIds: allOrgWorkspaceIds } : {}),
  });
  const duplicateConflicts = useMemo(
    () => computeDuplicateIdentifierConflicts(allOrgRecords, workspaceNameMap),
    [allOrgRecords, workspaceNameMap],
  );

  const filterAndCreate = useProductsFilterAndCreateState(
    session?.activeWorkspaceId,
    params.defaultViewMode,
  );
  const {
    searchQuery,
    brandFilter,
    statusFilter,
    sectorFilter,
    validationFilter,
    sortBy,
    showNewForm,
    setShowNewForm,
    newAi,
    newKeyInput,
    setNewKeyInput,
    newTitle,
    setNewTitle,
    newBrand,
    setNewBrand,
    newDefaultUrl,
    newWorkspaceId,
    setNewFormError,
    setCreatingProduct,
  } = filterAndCreate;

  const [showRecallConfirm, setShowRecallConfirm] = useState(false);
  const [recallReason, setRecallReason] = useState('');
  const [recallOverrideUrl, setRecallOverrideUrl] = useState('');
  const [recallError, setRecallError] = useState<string | null>(null);
  const [recallSubmitting, setRecallSubmitting] = useState(false);
  const [recallLastResult, setRecallLastResult] = useState<RecallActionOutcome | null>(null);
  const [qrTableId, setQrTableId] = useState<Gs1SymbolSpecTableId>('TABLE_5_46');
  const [lastDownloadedQr, setLastDownloadedQr] = useState<{
    readonly format: QrExportFormat;
    readonly assets: GeneratedGs1QrPrintAssets;
  } | null>(null);
  const [linkFormError, setLinkFormError] = useState<string | null>(null);
  const [linkFormSuccess, setLinkFormSuccess] = useState<string | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);

  const facetCounts = useMemo(() => computeProductFacetCounts(records), [records]);
  const filteredRecords = filterWorkspaceIdentifierRecords({
    records,
    searchQuery,
    brandFilter,
    statusFilter,
    sectorFilter,
    validationFilter,
    workspaceFilter,
    sortBy,
  });
  const selectedRecord = selectedKey
    ? (records.find((r) => r.identifierKey === selectedKey) ?? null)
    : null;
  const workingLinks: readonly DraftTargetLink[] = selectedRecord
    ? (selectedRecord.draft?.links ?? selectedRecord.links ?? [])
    : [];
  const liveLinks: readonly DraftTargetLink[] = selectedRecord?.links ?? [];
  const isControlled = params.initialRecords !== undefined;
  const promotionTracker = usePendingPromotionTracker({
    records,
    selectedRecordKey: selectedRecord?.identifierKey,
    isControlled,
    onDiscardDraft: params.onDiscardDraft,
    session,
    selectedRecord,
    canEdit,
    setRecords,
    setSelectedKey,
    pushProductPath: (path) => nav.pushProductPath(path),
    setLinkFormSuccess,
  });

  const applyDraftLinksUpdate = async (
    nextLinks: readonly DraftTargetLink[],
    nextRules?: IdentifierRecord['rules'],
    nextSector?: SectorId,
  ): Promise<boolean> => {
    setLinkFormError(null);
    setLinkFormSuccess(null);
    return stageDraftLinksAction({
      session,
      selectedRecord,
      nextLinks,
      ...(nextRules !== undefined ? { nextRules } : {}),
      ...(nextSector !== undefined ? { nextSector } : {}),
      isControlled,
      onStageDraftLinks: params.onStageDraftLinks,
      setLinkFormError,
      setLinkFormSuccess,
      setSavingDraft,
      onPendingPromotionStarted: (identifierKey, scheduledMessage) =>
        promotionTracker.setPendingPromotion({ identifierKey, scheduledMessage }),
      onLocalDraftUpdated: (identifierKey, nextDraft, uid, updatedSector, stagedOverride) =>
        setRecords((prev) =>
          applyLocalDraftRecordUpdate(
            prev,
            identifierKey,
            nextDraft,
            uid,
            updatedSector,
            stagedOverride,
          ),
        ),
    });
  };

  const editState = useProductEditState({
    session,
    initialEditMode: params.initialEditMode,
    selectedRecord,
    workingLinks,
    setSelectedKey,
    setLinkFormError,
    setLinkFormSuccess,
    setSavingDraft,
    onSetScheduledChange: params.onSetScheduledChange,
    onCancelScheduledChange: params.onCancelScheduledChange,
    onScheduledRulesUpdated: (identifierKey, nextRules, version) =>
      setRecords((prev) =>
        prev.map((rec) =>
          rec.identifierKey === identifierKey
            ? {
                ...rec,
                version,
                rules: nextRules,
                ...(rec.draft !== undefined ? { draft: { ...rec.draft, rules: nextRules } } : {}),
              }
            : rec,
        ),
      ),
    applyDraftLinksUpdate,
  });

  const handleCreateProduct = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setNewFormError(null);
    await createProductDraftAction({
      session,
      targetWorkspaceId: newWorkspaceId || session?.activeWorkspaceId,
      newAi,
      newKeyInput,
      newTitle,
      newBrand,
      newDefaultUrl,
      isControlled,
      existingRecords: allOrgRecords,
      knownWorkspaceIds: allOrgWorkspaceIds,
      workspaceNameMap,
      setNewFormError,
      setCreatingProduct,
      onCreated: (identifierKey, localRecord) => {
        if (localRecord !== undefined) {
          setRecords((prev) => [
            ...prev.filter((r) => r.identifierKey !== localRecord.identifierKey),
            localRecord,
          ]);
        }
        setSelectedKey(identifierKey);
        editState.setIsEditing(false);
        const needsCheck = !isControlled || localRecord?.draft !== undefined;
        if (needsCheck) {
          promotionTracker.setPendingPromotion({ identifierKey, scheduledMessage: null });
          setLinkFormSuccess('Checking…');
        } else {
          setLinkFormSuccess('Saved — Live now');
        }
        setNewKeyInput('');
        setNewTitle('');
        setNewBrand('');
        setShowNewForm(false);
        nav.pushProductPath(`/products/${encodeURIComponent(identifierKey)}`);
      },
    });
  };

  const replaceLocalRecord = (updatedLocal?: IdentifierRecord): void => {
    if (!updatedLocal) return;
    setRecords((prev) =>
      prev.map((rec) => (rec.identifierKey === updatedLocal.identifierKey ? updatedLocal : rec)),
    );
  };

  const handleConfirmSetRecall = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setRecallError(null);
    await confirmProductRecallAction({
      session,
      selectedRecord,
      canRecall,
      recallReason,
      recallOverrideUrl,
      effectiveResolverHost,
      onSetRecall: params.onSetRecall,
      setRecallError,
      setRecallSubmitting,
      onRecalled: (updatedLocal, outcome) => {
        replaceLocalRecord(updatedLocal);
        setRecallLastResult(outcome);
        setShowRecallConfirm(false);
        setRecallReason('');
        if (selectedRecord)
          nav.pushProductPath(`/products/${encodeURIComponent(selectedRecord.identifierKey)}`);
      },
    });
  };

  const handleClearRecall = async (): Promise<void> => {
    setRecallError(null);
    await clearProductRecallAction({
      session,
      selectedRecord,
      canRecall,
      effectiveResolverHost,
      onClearRecall: params.onClearRecall,
      setRecallError,
      setRecallSubmitting,
      onCleared: (updatedLocal, outcome) => {
        replaceLocalRecord(updatedLocal);
        setRecallLastResult(outcome);
      },
    });
  };

  const currentQrPreview = useMemo<GeneratedGs1QrPrintAssets | null>(
    () => computeProductQrPreview(selectedRecord, effectiveResolverHost, qrTableId),
    [selectedRecord, effectiveResolverHost, qrTableId],
  );
  const handleDownloadQr = (format: QrExportFormat, xDimensionMm?: number): void =>
    downloadProductQrAsset({
      selectedRecord,
      effectiveResolverHost,
      qrTableId,
      format,
      xDimensionMm,
      onDownloaded: setLastDownloadedQr,
    });
  const csvCtrl = useProductsCsvController({
    session,
    effectiveResolverHost,
    records,
    allOrgRecords,
    knownWorkspaceIds: allOrgWorkspaceIds,
    workspaceNameMap,
    filteredRecords,
    isControlled,
    setRecords,
    setSelectedKey,
  });
  const nav = useProductsRouterNavigation({
    initialSelectedKey: params.initialSelectedKey,
    initialEditMode: params.initialEditMode,
    selectedRecordKey: selectedRecord?.identifierKey,
    showRecallConfirm,
    editingIndex: editState.editingIndex,
    isEditing: editState.isEditing,
    showNewForm,
    showCsvImportPanel: csvCtrl.showCsvImportPanel,
    setSelectedKey,
    setIsEditing: editState.setIsEditing,
    setShowAdvanced: editState.setShowAdvanced,
    setEditingIndex: editState.setEditingIndex,
    setShowRecallConfirm,
    setRecallError,
    setShowNewForm,
    setNewFormError,
    setShowCsvImportPanel: csvCtrl.setShowCsvImportPanel,
    setCsvImportError: csvCtrl.setCsvImportError,
    setCsvImportSuccess: csvCtrl.setCsvImportSuccess,
    setLinkFormError,
    setLinkFormSuccess,
    resetLinkForm: editState.resetLinkForm,
    handleOpenProduct: editState.handleOpenProduct,
    handleStartEditingProduct: editState.handleStartEditingProduct,
    handleCancelEditingProduct: editState.handleCancelEditingProduct,
    handleSaveProductEdit: editState.handleSaveProductEdit,
    onExportCsv: csvCtrl.handleExportCsv,
  });
  return {
    session,
    availableWorkspaces,
    workspaceScope,
    setWorkspaceScope,
    ...workspaceMeta,
    workspaceFilter,
    setWorkspaceFilter,
    canEdit,
    canRecall,
    canExport,
    loading,
    effectiveResolverHost,
    filteredRecords,
    allOrgRecords,
    duplicateConflicts,
    selectedRecord,
    workingLinks,
    liveLinks,
    facetCounts,
    listenerError,
    ...filterAndCreate,
    ...editState,
    ...csvCtrl,
    ...promotionTracker,
    ...nav,
    setShowNewForm: nav.handleSetShowNewForm,
    setShowCsvImportPanel: nav.handleSetShowCsvImportPanel,
    handleOpenProduct: nav.handleOpenProductWithRoute,
    handleStartEditingProduct: nav.handleStartEditingProductWithRoute,
    handleCancelEditingProduct: nav.handleCancelEditingProductWithRoute,
    handleSaveProductEdit: nav.handleSaveProductEditWithRoute,
    setShowRecallConfirm: nav.handleSetShowRecallConfirm,
    showRecallConfirm,
    recallReason,
    setRecallReason,
    recallOverrideUrl,
    setRecallOverrideUrl,
    recallError,
    setRecallError,
    recallSubmitting,
    recallLastResult,
    setRecallLastResult,
    qrTableId,
    setQrTableId,
    lastDownloadedQr,
    linkFormError,
    setLinkFormError,
    linkFormSuccess,
    setLinkFormSuccess,
    savingDraft,
    setSelectedKey,
    handleCreateProduct,
    applyDraftLinksUpdate,
    handleConfirmSetRecall,
    handleClearRecall,
    currentQrPreview,
    handleDownloadQr,
  };
}
